import { classNames } from '@progress/kendo-react-common'
import StepperInput from 'components/Stepper'
import Translation from 'components/Translation'
import { useAppContext } from 'context/AppContext'
import { useAuth } from 'context/AuthContext'
import { useTenantContext } from 'context/TenantContext'
import { DISCOUNT_TYPE } from 'pages/dashboard/coupons'
import React, { useEffect, useState } from 'react'
import { getTranslation } from 'utils/language-helper'
import { getCurrencySymbol, toYmd } from 'utils/string-localization-helper'

import { ICouponModal } from '.'
import CouponCard from './CouponCard'

export interface IBasicInfo {
    onSubmit: any
}

export const REDEMPTION_OPTIONS = [
    {
        value: '1',
        title: 'One use per customer',
        key: 'one_use_per_customer',
    },
    // {
    //     value: '2',
    //     title: 'Limited no. of uses across all clients',
    //     key: 'limited_for_all_clients',
    // },
    {
        value: '3',
        title: 'Total number of uses across all clients (one use per client)',
        key: 'total_use_for_all_clients_1',
    },
    {
        value: '4',
        title: 'Total number of uses across all clients (no limit per client)',
        key: 'total_use_for_all_clients_2',
    },
    {
        value: '5',
        title: 'Unlimited uses for all clients',
        key: 'unlimited_for_all_clients',
    },
]

const BasicInformation: React.FC<ICouponModal> = ({
    formState,
    selectedOption,
    setSelectedOption,
    setIsDirty,
}) => {
    const { errors } = formState
    const { CreateCouponModal } = useAuth()
    const { locations, tenant } = useTenantContext()
    const { translations } = useAppContext()
    const { attributes, setAttributes, is_open } = CreateCouponModal

    const [hasEndDate, setHasEndDate] = useState(!!attributes?.expiryDate)
    const [redemptionLimit, setRedemptionLimit] = useState(0)
    const [email, setEmail] = useState('')

    useEffect(() => {
        if (is_open) {
            const usage_limit = attributes?.usageLimit
            const usage_per_client = attributes?.usagePerClient
            let redemption_option = 1
            if (usage_limit == 2000000 && usage_per_client != 1) {
                redemption_option = 2
            } else if (
                usage_limit == 1000000 &&
                usage_per_client == 1000000
            ) {
                redemption_option = 5
            } else if (usage_per_client === 1 && usage_limit !== 1000000) {
                redemption_option = 3
            } else if (
                usage_per_client === 2000000 &&
                usage_limit !== 2000000
            ) {
                redemption_option = 4
            }
            setSelectedOption(redemption_option)
        }
    }, [is_open])

    useEffect(() => {
        if (!attributes?.discountType) {
            setAttributes({
                ...attributes,
                discountType: DISCOUNT_TYPE.PERCENT,
            })
        }
    }, [attributes?.discountType])

    const changeHandler = (e: React.ChangeEvent<any>, value: string) => {
        setIsDirty(true)
        setAttributes({
            ...attributes,
            [e.target.name]: value,
        })
    }

    const handleRedemptionSelect = (value: number, limit: number) => {
        let usageLimit = 0
        let usagePerClient = 0

        switch (value) {
            case 1:
                usageLimit = 1000000
                usagePerClient = 1

                break
            case 2:
                usageLimit = 2000000
                usagePerClient = limit

                break
            case 3:
                usageLimit = limit
                usagePerClient = 1

                break
            case 4:
                usageLimit = limit
                usagePerClient = 2000000
                break

            case 5:
                usageLimit = 1000000
                usagePerClient = 1000000

                break
            default:
        }

        setAttributes({ ...attributes, usageLimit, usagePerClient })
    }

    const getOptionsFields = () => {
        return (
            <div className="p-2">
                <div className="p-2 bg-gray-200 rounded-md">
                    <div className="flex w-full items-center justify-around mt-3">
                        <>
                            <div className="w-1/3">Usage Limit</div>
                            <div className="w-2/3">
                                <StepperInput
                                    default_value={
                                        attributes?.usageLimit as number
                                    }
                                    disabled={false}
                                    max_value={1000000}
                                    onChange={(v: number) => {
                                        setIsDirty(true)
                                        setRedemptionLimit(v)
                                        handleRedemptionSelect(
                                            selectedOption,
                                            v
                                        )
                                    }}
                                />
                            </div>
                        </>
                    </div>
                </div>
            </div>
        )
    }

    const getLeftFormContainer = () => (
        <div id="leftContainer" className="w-1/2 ">
            <div className="w-full pb-5">
                <fieldset className="lg:pb-0">
                    <Translation
                        render_as="label"
                        htmlFor="first-name"
                        className={classNames(
                            'block text-sm',
                            errors?.code?.type ? 'text-red-700' : ''
                        )}
                        content_key="code"
                    />
                    <div className="flex">
                        <input
                            type="text"
                            id="code"
                            autoComplete="code"
                            name="code"
                            className={classNames(
                                'p-2 text-sm mt-1 focus:ring-primary-light focus:border-primary-light block w-full  border-gray-300 rounded-md',
                                errors.code?.type
                                    ? 'border-red-300 bg-red-100'
                                    : ''
                            )}
                            defaultValue={
                                (attributes?.code as string) || ''
                            }
                            onChange={(e) =>
                                changeHandler(e, e.target.value)
                            }
                        />
                    </div>
                    {errors.code?.type === 'required' && (
                        <Translation
                            render_as="span"
                            className="text-sm text-red-700"
                            content_key="code_required"
                        />
                    )}
                </fieldset>
            </div>
            <div className="w-full pb-5">
                <fieldset>
                    <Translation
                        render_as="label"
                        htmlFor="notes"
                        className={classNames(
                            'block text-sm',
                            errors.notes?.type ? 'text-red-700' : ''
                        )}
                        content_key="description"
                    />
                    <textarea
                        id="notes"
                        className={classNames(
                            'placeholder-gray-300 mt-1 h-40 focus:ring-primary-light focus:border-primary-light block w-full  rounded-md px-2 py-1 text-sm',
                            errors.name?.type
                                ? 'border-red-300 bg-red-100'
                                : ''
                        )}
                        placeholder={getTranslation(
                            'coupon_description',
                            translations
                        )}
                        name="description"
                        defaultValue={
                            (attributes?.description as string) || ''
                        }
                        onChange={(e) => changeHandler(e, e.target.value)}
                    />
                    {errors.notes?.type === 'length' && (
                        <Translation
                            render_as="span"
                            className="text-sm text-red-700"
                            content_key="desc_required"
                        />
                    )}
                </fieldset>
            </div>
            <div className="w-full pb-5 flex">
                <div className="w-full">
                    <fieldset className="lg:pb-0">
                        <Translation
                            render_as="label"
                            htmlFor="first-name"
                            className={classNames(
                                'block text-sm'
                                // errors.first_name?.type ? 'text-red-700' : ''
                            )}
                            content_key="redemption_period"
                        />

                        <div className="flex w-full gap-4 items-center">
                            <div className="w-full ">
                                <input
                                    min={new Date().toLocaleDateString(
                                        'en-ca'
                                    )}
                                    type="date"
                                    id="effectiveDate"
                                    name="effectiveDate"
                                    autoComplete="effectiveDate"
                                    className={classNames(
                                        'mt-1 text-sm focus:ring-primary-light focus:border-primary-light block w-full  rounded-md',
                                        errors.first_name?.type
                                            ? 'border-red-300 bg-red-100'
                                            : '',
                                        'mr-1 w-full'
                                    )}
                                    defaultValue={
                                        (attributes?.effectiveDate as string) ||
                                        toYmd(new Date())
                                    }
                                    onChange={(e) =>
                                        changeHandler(
                                            e,
                                            toYmd(new Date(e.target.value))
                                        )
                                    }
                                />
                            </div>
                            <div className="">
                                {!hasEndDate ? (
                                    <div className="flex items-center ">
                                        <i className="feather-plus text-primary-light text-sm mr-1" />
                                        <div
                                            className="text-primary-light font-medium text-sm cursor-pointer"
                                            onClick={() => {
                                                setHasEndDate(
                                                    (prev) => !prev
                                                )
                                            }}
                                        >
                                            <Translation
                                                render_as="div"
                                                content_key="set_end_date_label"
                                                className="setendlabel"
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-center">
                                        <input
                                            min={new Date().toLocaleDateString(
                                                'en-ca'
                                            )}
                                            type="date"
                                            id="expiryDate"
                                            name="expiryDate"
                                            autoComplete="first_name"
                                            onChange={(e) =>
                                                changeHandler(
                                                    e,
                                                    toYmd(
                                                        new Date(
                                                            e.target.value
                                                        )
                                                    )
                                                )
                                            }
                                            defaultValue={
                                                (attributes?.expiryDate as string) ||
                                                ''
                                            }
                                            className={classNames(
                                                'mt-1 text-sm focus:ring-primary-light focus:border-primary-light block w-full  rounded-md',
                                                errors.first_name?.type
                                                    ? 'border-red-300 bg-red-100'
                                                    : ''
                                            )}
                                        />
                                        <i
                                            className="feather-x-circle text-primary-light text-sm cursor-pointer ml-1"
                                            onClick={() => {
                                                setIsDirty(true)
                                                delete attributes?.expiryDate
                                                setAttributes({
                                                    ...attributes,
                                                })
                                                setHasEndDate(false)
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        {errors.code?.type === 'required' && (
                            <Translation
                                render_as="span"
                                className="text-sm text-red-700"
                                content_key="first_name_required"
                            />
                        )}
                    </fieldset>
                </div>
            </div>
            <div className="w-full pb-5">
                <fieldset className="">
                    <Translation
                        render_as="label"
                        htmlFor="notes"
                        className={classNames(
                            'block text-sm',
                            errors.notes?.type ? 'text-red-700' : ''
                        )}
                        content_key="redemption_limit"
                    />
                    <select
                        className={classNames(
                            'mt-1 p-2 focus:ring-primary-light focus:border-primary-light block w-full  rounded-md text-sm',
                            errors.first_name?.type
                                ? 'border-red-300 bg-red-100'
                                : ''
                        )}
                        value={selectedOption}
                        onChange={(e) => {
                            setIsDirty(true)
                            setSelectedOption(+e.target.value)
                            handleRedemptionSelect(
                                +e.target.value,
                                redemptionLimit
                            )
                        }}
                    >
                        {REDEMPTION_OPTIONS.map((item, index) => (
                            <option key={item.value} value={+item.value}>
                                {translations[item.key] || item.title}
                            </option>
                        ))}
                    </select>

                    {errors.notes?.type === 'length' && (
                        <Translation
                            render_as="span"
                            className="text-sm text-red-700"
                            content_key="illegal_values"
                        />
                    )}
                </fieldset>
            </div>
            {(selectedOption === 3 || selectedOption === 4) &&
                getOptionsFields()}
        </div>
    )

    const getRightFormContainer = () => (
        <div id="rightContainer" className="ml-4 w-1/2">
            <div className="w-full pb-5">
                <fieldset className="lg:pb-0">
                    <Translation
                        render_as="label"
                        htmlFor=""
                        className={classNames(
                            'block text-sm mb-1'
                            // errors.first_name?.type ? 'text-red-700' : ''
                        )}
                        content_key="coupon_preview"
                    />
                    <div
                        className="border-gray-300 border-dashed rounded-md border-2 couponcard"
                        onClick={() =>
                            document.getElementById('s')?.click()
                        }
                    >
                        <label htmlFor="img" className=" " id="s">
                            <CouponCard
                                code={attributes?.code as string}
                                discountType={
                                    attributes?.discountType as string
                                }
                                discountValue={
                                    attributes?.discountValue as string
                                }
                                expiryDate={
                                    attributes?.expiryDate as string
                                }
                                image_url={tenant?.bizLogoUrl}
                            />
                        </label>
                    </div>
                    <div
                        className="flex justify-between mt-2"
                        hidden={true}
                    >
                        <input
                            type={'email'}
                            placeholder="enter email"
                            onChange={(e) => setEmail(e.target.value)}
                            className="rounded-md border-gray-300 focus:ring-primary-light focus:border-primary-light text-sm p-2 w-7/12"
                        />
                        <button
                            type="button"
                            className="border-gray-300 p-2 text-sm rounded-md"
                            style={{ borderWidth: '1px' }}
                            onClick={() => {
                                if (email.length > 0)
                                    window
                                        .open(`mailto:${email}`, '__blank')
                                        ?.focus()
                            }}
                        >
                            Send Email
                        </button>
                    </div>
                </fieldset>
            </div>
            <div className="w-full pb-5">
                <fieldset className="pb-6 lg:pb-0">
                    <Translation
                        render_as="label"
                        htmlFor="discount"
                        className={classNames(
                            'block text-sm'
                            // errors.first_name?.type ? 'text-red-700' : ''
                        )}
                        content_key="discount"
                    />
                    <div className="flex discount-wrap">
                        <select
                            className="w-1/4 text-sm mt-1 focus:ring-primary-light focus:border-primary-light   rounded-l-md"
                            defaultValue={
                                (attributes?.discountType as string) ||
                                DISCOUNT_TYPE.PERCENT
                            }
                            name="discountType"
                            onChange={(e) =>
                                changeHandler(e, e.target.value)
                            }
                        >
                            <option value={DISCOUNT_TYPE.PERCENT}>
                                % off &nbsp;
                            </option>
                            <option value={DISCOUNT_TYPE.EXACT_VALUE}>
                                {getCurrencySymbol()} off &nbsp;
                            </option>
                        </select>
                        <input
                            type="text"
                            id="discount"
                            autoComplete="discountValue"
                            name="discountValue"
                            className={classNames(
                                'text-sm mt-1 focus:ring-primary-light focus:border-primary-light block w-full  rounded-r-md',
                                errors.first_name?.type
                                    ? 'border-red-300 bg-red-100'
                                    : ''
                            )}
                            onChange={(e) =>
                                changeHandler(e, e.target.value)
                            }
                            defaultValue={
                                (attributes?.discountValue as string) || ''
                            }
                        />
                    </div>
                    {errors.code?.type === 'required' && (
                        <Translation
                            render_as="span"
                            className="text-sm text-red-700"
                            content_key="first_name_required"
                        />
                    )}
                </fieldset>
            </div>
            <div className="w-full">
                <fieldset className="">
                    <Translation
                        render_as="label"
                        htmlFor="locationIds"
                        className={classNames(
                            'block text-sm',
                            errors.notes?.type ? 'text-red-700' : ''
                        )}
                        content_key="locations"
                    />
                    <select
                        className={classNames(
                            'text-sm mt-1 focus:ring-primary-light focus:border-primary-light block w-full  rounded-md',
                            errors.first_name?.type
                                ? 'border-red-300 bg-red-100'
                                : ''
                        )}
                        name="locationIds"
                        defaultValue={
                            (attributes?.locationIds as string) || ''
                        }
                        onChange={(e) => {
                            setAttributes({ //why is this a single select?
                                ...attributes,
                                locationIds: e.target.value.length > 0 ? [e.target.value] : []
                            })
                        }}
                    >
                        <option value={""}>All Locations</option>
                        {locations?.map((item: any, index: number) => (
                            <option key={index} value={item.id}>
                                {item.name}
                            </option>
                        ))}
                    </select>

                    {errors.notes?.type === 'length' && (
                        <Translation
                            render_as="span"
                            className="text-sm text-red-700"
                            content_key="illegal_values"
                        />
                    )}
                </fieldset>
            </div>
        </div>
    )

    return (
        <>
            <div className="text-primary-darker text-md">
                <Translation content_key="coupon_basic_info" />
            </div>
            <div id="formContainer" className="flex mt-2 modal-form">
                {getLeftFormContainer()}
                {getRightFormContainer()}
            </div>
        </>
    )
}

export default BasicInformation
